import React from "react";
import { Box, Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  useGetDashboardStatusQuery,
} from "./operatorDashboardAPISlice";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";

export default function OperatorDashboardStatus() {
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const {
    data: dashboardStatus,
    isLoading: isDashboardStatusLoading,
    isError: isDashboardStatusError,
  } = useGetDashboardStatusQuery(userTimezone, {
    pollingInterval: 60000,
  });

  if (isDashboardStatusLoading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: "12px", p: 2 }}>
        <CircularProgress size={24} sx={{ color: "#fff" }} />
        <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: "0.88rem" }}>
          Loading current PS and LS state
        </Typography>
      </Box>
    );
  }

  if (isDashboardStatusError) {
    return (
      <>
        <Typography variant="body2" sx={{ color: "rgba(255,165,0,0.8)", p: 1 }}>
          Dashboard status unavailable.
        </Typography>
        <DashboardFractionValues dashboardStatus={dashboardStatus} />
      </>
    );
  }

  // The counts are devices installed in DSM and In Field in IMS. When IMS can't be reached
  // they fall back to DSM alone and read higher; say so rather than show a different
  // number without comment (NEU-494).
  return (
    <>
      {dashboardStatus?.ims_crosscheck_active === false && (
        <Typography variant="body2" sx={{ color: "rgba(255,165,0,0.8)", pt: 1 }}>
          IMS couldn't be reached, so these counts include devices IMS hasn't confirmed as In Field.
        </Typography>
      )}
      <DashboardFractionValues dashboardStatus={dashboardStatus} />
    </>
  );
}

function DashboardFractionValues({ dashboardStatus }) {
  const defaultStatus = {
    devices_connected: 0,
    total_devices: 0,
    total_geysers: 0,
    consistent_devices: 0,
    total_meter_control: 0,
    baseline_devices: 0,
    total_geysers_on: 0,
    average_hot_water_index: 0,
  };
  const status = { ...defaultStatus, ...dashboardStatus };

  const kpiCellSx = {
    background: "#161B22",
    border: "1px solid rgba(255,255,255,0.06)",
    borderRadius: "10px",
    padding: "16px 18px",
  };
  const labelSx = {
    display: "block",
    fontSize: "0.68rem",
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "rgba(255,255,255,0.45)",
    mb: "6px",
  };
  const valueSx = {
    display: "block",
    fontSize: "1.4rem",
    fontWeight: 700,
    color: "#fff",
    fontVariantNumeric: "tabular-nums",
    letterSpacing: "-0.02em",
  };
  const ofSx = {
    color: "rgba(255,255,255,0.35)",
    fontWeight: 500,
    fontSize: "0.95rem",
    marginLeft: "4px",
  };
  const infoIconSx = {
    fontSize: "13px",
    color: "rgba(255,255,255,0.3)",
    cursor: "help",
    flexShrink: 0,
    "&:hover": { color: "rgba(255,255,255,0.65)" },
  };

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(5, 1fr)",
        gap: "12px",
        width: "100%",
        mt: "20px",
        mb: "10px",
      }}
    >
      <Box sx={kpiCellSx}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "4px", mb: "6px" }}>
          <Typography component="span" sx={{ ...labelSx, mb: 0 }}>Connected</Typography>
          <Tooltip title="Installed devices currently sending telemetry, out of all installed devices." placement="top" arrow>
            <InfoOutlinedIcon sx={infoIconSx} />
          </Tooltip>
        </Box>
        <Typography component="span" sx={valueSx}>
          {status.devices_connected}
          {/* Out of installed devices, the same kind of record as the count; it was shown
              over the geyser count (NEU-1125). */}
          <Box component="span" sx={ofSx}>/ {status.total_devices}</Box>
        </Typography>
      </Box>
      <Box sx={kpiCellSx}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "4px", mb: "6px" }}>
          <Typography component="span" sx={{ ...labelSx, mb: 0 }}>≥ Consistency Threshold</Typography>
          <Tooltip title="Devices meeting the minimum data-consistency requirement to be eligible for load control." placement="top" arrow>
            <InfoOutlinedIcon sx={infoIconSx} />
          </Tooltip>
        </Box>
        <Typography component="span" sx={valueSx}>
          {status.consistent_devices}
          <Box component="span" sx={ofSx}>/ {status.devices_connected}</Box>
        </Typography>
      </Box>
      <Box sx={kpiCellSx}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "4px", mb: "6px" }}>
          <Typography component="span" sx={{ ...labelSx, mb: 0 }}>Under Control</Typography>
          <Tooltip title="Geysers the DSM system is actively controlling right now (Control Status other than Nil or Recovering), out of all eligible devices." placement="top" arrow>
            <InfoOutlinedIcon sx={infoIconSx} />
          </Tooltip>
        </Box>
        <Typography component="span" sx={valueSx}>
          {status.total_meter_control}
          <Box component="span" sx={ofSx}>/ {status.consistent_devices - status.baseline_devices}</Box>
        </Typography>
      </Box>
      <Box sx={kpiCellSx}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "4px", mb: "6px" }}>
          <Typography component="span" sx={{ ...labelSx, mb: 0 }}>Thermo On</Typography>
          <Tooltip title="Geysers where the thermostat element is currently energised, out of connected devices." placement="top" arrow>
            <InfoOutlinedIcon sx={infoIconSx} />
          </Tooltip>
        </Box>
        <Typography component="span" sx={valueSx}>
          {status.total_geysers_on}
          <Box component="span" sx={ofSx}>/ {status.devices_connected}</Box>
        </Typography>
      </Box>
      <Box sx={kpiCellSx}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "4px", mb: "6px" }}>
          <Typography component="span" sx={{ ...labelSx, mb: 0 }}>Average HWI</Typography>
          <Tooltip title="Mean Hot Water Index across all connected geysers — indicates current stored hot water availability." placement="top" arrow>
            <InfoOutlinedIcon sx={infoIconSx} />
          </Tooltip>
        </Box>
        <Typography component="span" sx={valueSx}>
          {(status.average_hot_water_index || 0).toFixed(1)}
        </Typography>
      </Box>
    </Box>
  );
}
